import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, X, Star, Zap, Crown, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const Pricing = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchPricing();
    fetchSubscription();
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/pricing');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscription');
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
    setLoading(false);
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const response = await api.get(`/checkout/status/${sessionId}`);
      const { payment_status, status } = response.data;

      if (payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Payment successful! Your subscription is now active.' });
        fetchSubscription();
        toast({
          title: "Payment Successful!",
          description: "Your subscription has been activated.",
        });
        return;
      } else if (status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        return;
      }

      setPaymentStatus({ status: 'processing', message: 'Processing your payment...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') {
      toast({
        title: "Free Plan",
        description: "You're already on the free plan!",
      });
      return;
    }

    setProcessingPlan(planId);

    try {
      const originUrl = window.location.origin;
      const response = await api.post('/checkout/create-session', {
        plan_id: planId,
        origin_url: originUrl
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to start checkout. Please try again.",
        variant: "destructive"
      });
    }
    setProcessingPlan(null);
  };

  const planConfigs = {
    free: { icon: Star, color: 'from-gray-500 to-gray-600', popular: false },
    pro: { icon: Zap, color: 'from-emerald-500 to-teal-500', popular: true },
    enterprise: { icon: Crown, color: 'from-purple-500 to-indigo-500', popular: false }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Payment Status Banner */}
      {paymentStatus && (
        <Card className={`border-2 ${
          paymentStatus.status === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
          paymentStatus.status === 'processing' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' :
          'border-red-500 bg-red-50 dark:bg-red-900/20'
        }`}>
          <CardContent className="py-4 flex items-center space-x-3">
            {paymentStatus.status === 'success' && <CheckCircle className="w-6 h-6 text-green-600" />}
            {paymentStatus.status === 'processing' && <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />}
            {(paymentStatus.status === 'expired' || paymentStatus.status === 'timeout') && <XCircle className="w-6 h-6 text-red-600" />}
            <span className={`font-medium ${
              paymentStatus.status === 'success' ? 'text-green-700 dark:text-green-400' :
              paymentStatus.status === 'processing' ? 'text-blue-700 dark:text-blue-400' :
              'text-red-700 dark:text-red-400'
            }`}>
              {paymentStatus.message}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
          Choose Your Plan
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Select the perfect plan for your diabetes management journey. Upgrade or downgrade anytime.
        </p>
      </div>

      {/* Current Plan Badge */}
      {currentSubscription && (
        <div className="flex justify-center">
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 px-6 py-2 text-base">
            Current Plan: {currentSubscription.plan_name}
          </Badge>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans && Object.entries(plans).map(([planId, plan]) => {
          const config = planConfigs[planId] || planConfigs.free;
          const Icon = config.icon;
          const isCurrentPlan = currentSubscription?.plan_id === planId;
          const isProcessing = processingPlan === planId;

          return (
            <Card
              key={planId}
              className={`relative shadow-lg hover:shadow-2xl transition-all duration-300 border-2 dark:bg-gray-800 ${
                config.popular
                  ? 'border-emerald-500 scale-105'
                  : isCurrentPlan
                  ? 'border-blue-500'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {config.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-1 shadow-lg">
                    Most Popular
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-8 pt-8">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${config.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl mb-2 dark:text-white">{plan.name}</CardTitle>
                <div className="flex items-baseline justify-center space-x-1">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    ${plan.price.toFixed(2)}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-600 dark:text-gray-400">/month</span>
                  )}
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Features */}
                <div className="space-y-3">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white mb-3">Features included:</p>
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Limits Info */}
                {plan.limits && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {plan.limits.ai_requests === -1 ? 'Unlimited AI requests' : `${plan.limits.ai_requests} AI requests/month`}
                    </p>
                  </div>
                )}

                {/* CTA Button */}
                <Button
                  onClick={() => handleSelectPlan(planId)}
                  disabled={isCurrentPlan || isProcessing}
                  className={`w-full mt-6 h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all ${
                    config.popular
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
                      : isCurrentPlan
                      ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                  }`}
                >
                  {isProcessing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : plan.price === 0 ? (
                    'Get Started Free'
                  ) : (
                    'Subscribe Now'
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* FAQ Section */}
      <Card className="shadow-md border-0 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 mt-12">
        <CardHeader>
          <CardTitle className="text-2xl text-center dark:text-white">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Can I change my plan anytime?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Yes! You can upgrade, downgrade, or cancel your subscription at any time. Changes take effect at the start of your next billing cycle.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What payment methods do you accept?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We accept all major credit cards (Visa, Mastercard, American Express) through our secure Stripe payment gateway.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Is my health data secure?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Absolutely. We use bank-level encryption and are HIPAA compliant. Your health data is never shared without your explicit consent.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Do you offer refunds?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Yes, we offer a 30-day money-back guarantee. If you're not satisfied, contact us for a full refund within 30 days of purchase.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Badge */}
      <div className="flex justify-center items-center space-x-4 text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-2">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
          </svg>
          <span className="text-sm">Secure Payment</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm">Powered by</span>
          <span className="font-semibold">Stripe</span>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
